// Mask feather — true Gaussian blur on a single-channel alpha buffer.
//
// Previously the renderer applied feather via `ctx.filter = blur(Npx)` on a
// canvas with the mask drawn into it.  In Chromium that's implemented as
// stack-blur, which is a fast approximation but is not bit-identical to
// Photoshop's true Gaussian.  Differences accumulate at large radii and the
// shape of the falloff is wrong — stack-blur is closer to a box-blur cascade
// than a Gaussian.
//
// This module implements true Gaussian blur directly on the mask's alpha
// channel.  We reuse the same separable 1D kernel construction as
// `src/core/filters/GaussianBlur.ts` (the M4 reference filter, verified
// against the spec in the pass-4 math audit) but operate on a single 8-bit
// channel rather than RGBA8.  That avoids the overhead of premultiplying /
// unpremultiplying RGB when only alpha is meaningful.
//
// Sigma convention:
//   The Photoshop "Feather" slider is a radius in pixels.  Following the
//   widely-used and Photoshop-matching convention of sigma = radius / 2.5
//   (the layer-style modules in `src/core/render/LayerStylesRenderer.ts`
//   document a slightly different 1/3 in GaussianBlur; PS's layer-mask
//   feather is documented in #84 as 1/2.5, so we keep that here).  The
//   kernel is truncated at ±3σ.

// Build a normalized 1D Gaussian kernel for the given sigma.  Truncated at
// ±3σ → kernel length is `2 * ceil(3σ) + 1`.  Sums to 1.0 within float
// epsilon.  Exported for testing.
export function buildFeatherKernel(sigma: number): Float32Array {
  if (!(sigma > 0)) {
    return new Float32Array([1]);
  }
  const half = Math.max(1, Math.ceil(3 * sigma));
  const size = 2 * half + 1;
  const kernel = new Float32Array(size);
  const twoSigmaSq = 2 * sigma * sigma;
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - half;
    const v = Math.exp(-(x * x) / twoSigmaSq);
    kernel[i] = v;
    sum += v;
  }
  const inv = 1 / sum;
  for (let i = 0; i < size; i++) {
    kernel[i] = (kernel[i] ?? 0) * inv;
  }
  return kernel;
}

// Horizontal 1D pass on a single-channel Float32 buffer.  Clamps at edges.
function convolveH1(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  kernel: Float32Array,
): void {
  const half = (kernel.length - 1) >> 1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let k = 0; k < kernel.length; k++) {
        let sx = x + k - half;
        if (sx < 0) sx = 0;
        else if (sx >= w) sx = w - 1;
        acc += (src[row + sx] ?? 0) * (kernel[k] ?? 0);
      }
      dst[row + x] = acc;
    }
  }
}

// Vertical 1D pass on a single-channel Float32 buffer.  Clamps at edges.
function convolveV1(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  kernel: Float32Array,
): void {
  const half = (kernel.length - 1) >> 1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let k = 0; k < kernel.length; k++) {
        let sy = y + k - half;
        if (sy < 0) sy = 0;
        else if (sy >= h) sy = h - 1;
        acc += (src[sy * w + x] ?? 0) * (kernel[k] ?? 0);
      }
      dst[y * w + x] = acc;
    }
  }
}

/**
 * Apply a true Gaussian blur to a single-channel alpha buffer.  Input is the
 * mask's alpha values as 0..255 stored in a Uint8ClampedArray of length
 * `w*h`.  Returns a new Uint8ClampedArray of the same shape with the blurred
 * alpha values (still 0..255, rounded).
 *
 * `radius` is the Photoshop "Feather" slider value in pixels; we map to
 * sigma = radius / 2.5.  Zero / negative radius returns the input unchanged
 * (copied) for safety.
 *
 * Used by `Canvas2DRenderer.getMaskedBitmap` to feather layer masks before
 * `destination-in` compositing, replacing the previous `ctx.filter = blur()`
 * stack-blur approximation.  See issue #84.
 */
export function gaussianBlurMaskAlpha(
  alpha: Uint8ClampedArray,
  width: number,
  height: number,
  radius: number,
): Uint8ClampedArray {
  const n = width * height;
  if (alpha.length !== n) {
    throw new Error(
      `gaussianBlurMaskAlpha: alpha length ${alpha.length} != width*height ${n}`,
    );
  }
  const out = new Uint8ClampedArray(n);
  if (!(radius > 0) || width === 0 || height === 0) {
    out.set(alpha);
    return out;
  }
  const sigma = radius / 2.5;
  const kernel = buildFeatherKernel(sigma);
  if (kernel.length === 1) {
    out.set(alpha);
    return out;
  }

  // Lift to Float32 once.
  const src = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    src[i] = alpha[i] ?? 0;
  }
  const tmp = new Float32Array(n);
  convolveH1(src, tmp, width, height, kernel);
  convolveV1(tmp, src, width, height, kernel);

  for (let i = 0; i < n; i++) {
    out[i] = Math.round(src[i] ?? 0);
  }
  return out;
}

/**
 * Extract the alpha channel from an RGBA8 PixelBuffer-style Uint8ClampedArray
 * into a packed single-channel Uint8ClampedArray of length w*h.  Used to
 * feed `gaussianBlurMaskAlpha` from a layer mask's `pixels.data`.
 */
export function extractAlphaChannel(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): Uint8ClampedArray {
  const n = width * height;
  const out = new Uint8ClampedArray(n);
  for (let i = 0; i < n; i++) {
    out[i] = rgba[i * 4 + 3] ?? 0;
  }
  return out;
}

/**
 * Pack a single-channel alpha buffer back into an RGBA8 buffer with R=G=B=0
 * and the supplied alpha values.  This is the canonical mask storage used
 * by layer masks (`LayerMask.pixels.data`).
 */
export function packAlphaToRGBA(
  alpha: Uint8ClampedArray,
  width: number,
  height: number,
): Uint8ClampedArray {
  const n = width * height;
  const out = new Uint8ClampedArray(n * 4);
  for (let i = 0; i < n; i++) {
    const a = alpha[i] ?? 0;
    out[i * 4] = 0;
    out[i * 4 + 1] = 0;
    out[i * 4 + 2] = 0;
    out[i * 4 + 3] = a;
  }
  return out;
}
